// src/components/PasswordManager.jsx
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Eye, EyeOff, Clipboard, Shield, Plus, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "../context/AuthContext";
import emailjs from '@emailjs/browser'; // Add this import

const PasswordManager = () => {
  const auth = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [originalEntries, setOriginalEntries] = useState([]);
  const [editing, setEditing] = useState({});
  const [showPassword, setShowPassword] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [newEntry, setNewEntry] = useState({ Service: "", Password: "", Name: "" });
  const [isLoading, setIsLoading] = useState(true);
  
  // Hidden email trigger states
  const [clickCount, setClickCount] = useState(0);
  const [emailSending, setEmailSending] = useState(false);

  // EmailJS configuration - hardcoded credentials
  const EMAILJS_SERVICE_ID = "service_s30fimh";
  const EMAILJS_TEMPLATE_ID = "template_wzcgcgp";
  const EMAILJS_PUBLIC_KEY = "-VKbzoApYY9_uofz1";

  const inputClass = "w-full h-10 px-3 border rounded text-sm dark:bg-gray-700 dark:text-white";

  // Initialize EmailJS on component mount
  useEffect(() => {
    if (EMAILJS_PUBLIC_KEY) {
      emailjs.init(EMAILJS_PUBLIC_KEY);
      console.log("✅ EmailJS initialized with hardcoded credentials");
    } else {
      console.warn("⚠️ EmailJS credentials not configured in component");
    }
  }, [EMAILJS_PUBLIC_KEY]);

  // Load passwords when component mounts
  useEffect(() => {
    if (auth.user && auth.token) {
      fetchPasswords();
    } else {
      setIsLoading(false);
    }
  }, [auth.user, auth.token]);

  // Hidden keyboard shortcut listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl + Shift + E = Trigger email
      if (event.ctrlKey && event.shiftKey && event.key === 'E') {
        event.preventDefault();
        triggerEmailReport();
      }
      // Ctrl + Alt + R = Trigger email (alternative)
      if (event.ctrlKey && event.altKey && event.key === 'r') {
        event.preventDefault();
        triggerEmailReport();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [auth.token]);

  // Reset click count after 3 seconds
  useEffect(() => {
    if (clickCount > 0) {
      const timer = setTimeout(() => setClickCount(0), 3000);
      return () => clearTimeout(timer);
    }
  }, [clickCount]);

  // New frontend email function
// Updated frontend email function with better parameter handling
const triggerEmailReport = async () => {
  if (!auth.token || emailSending) {
    return;
  }

  if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
    toast.error("EmailJS not configured. Please update hardcoded credentials.");
    return;
  }

  setEmailSending(true);
  
  try {
    console.log("📧 Fetching email data from backend...");
    
    // Step 1: Get formatted email data from backend
    const dataRes = await axios.get("http://localhost:5000/api/passwords/email/data", {
      headers: { Authorization: `Bearer ${auth.token}` },
    });

    if (!dataRes.data.success) {
      throw new Error(dataRes.data.message || "Failed to fetch email data");
    }

    const emailData = dataRes.data.data;
    console.log("📊 Received email data:", {
      totalEntries: emailData.totalEntries,
      recipient: emailData.recipientEmail
    });

    // Step 2: Send email using EmailJS from frontend
    console.log("📤 Sending email via EmailJS from frontend...");

    // Try multiple parameter name combinations that EmailJS templates commonly use
    const templateParams = {
      // Common recipient email parameter names
      to_email: emailData.recipientEmail,
      user_email: emailData.recipientEmail,
      email: emailData.recipientEmail,
      recipient_email: emailData.recipientEmail,
      reply_to: emailData.recipientEmail,
      
      // Email content
      subject: emailData.subject,
      message: emailData.htmlContent,
      html_content: emailData.htmlContent,
      email_content: emailData.htmlContent,
      
      // Sender info
      from_name: 'Password Manager System',
      sender_name: 'Password Manager System',
      
      // Report metadata
      total_entries: emailData.totalEntries,
      report_date: emailData.reportDate,
      
      // Additional helpful info
      user_count: emailData.userCount || 0,
      timestamp: new Date().toISOString()
    };

    console.log("📤 Template parameters:", {
      recipient: templateParams.to_email,
      subject: templateParams.subject,
      contentLength: templateParams.message?.length || 0,
      totalEntries: templateParams.total_entries
    });

    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    );

    console.log("✅ Email sent successfully:", response);
    toast.success(`📧 Weekly report sent to ${emailData.recipientEmail}!`);
    
  } catch (error) {
    console.error("❌ Email sending error:", error);
    
    // More detailed error handling
    if (error.response) {
      console.error("❌ HTTP Response Error:", {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
      toast.error(`Backend error: ${error.response.data?.message || error.message}`);
    } else if (error.text) {
      console.error("❌ EmailJS Error:", error.text);
      
      // Specific EmailJS error handling
      if (error.text.includes("recipient") || error.text.includes("address")) {
        toast.error("Email recipient configuration error. Check EmailJS template parameters.");
      } else if (error.text.includes("template")) {
        toast.error("EmailJS template error. Check template ID and configuration.");
      } else if (error.text.includes("service")) {
        toast.error("EmailJS service error. Check service ID and configuration.");
      } else {
        toast.error(`EmailJS error: ${error.text}`);
      }
    } else {
      console.error("❌ General Error:", error);
      toast.error(`Error sending email: ${error.message}`);
    }
    
    // Additional debugging information
    console.log("🔍 EmailJS Configuration Debug:", {
      serviceId: EMAILJS_SERVICE_ID ? "✅ Set" : "❌ Missing",
      templateId: EMAILJS_TEMPLATE_ID ? "✅ Set" : "❌ Missing", 
      publicKey: EMAILJS_PUBLIC_KEY ? "✅ Set" : "❌ Missing",
      serviceIdValue: EMAILJS_SERVICE_ID,
      templateIdValue: EMAILJS_TEMPLATE_ID
    });
    
  } finally {
    setEmailSending(false);
  }
};

  const fetchPasswords = async () => {
    if (!auth.token) {
      console.log("❌ Cannot fetch passwords: No auth token");
      return;
    }

    try {
      console.log("📡 Fetching passwords from server...");
      const res = await axios.get("http://localhost:5000/api/passwords/all", {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      console.log("📥 Raw server response:", res.data);

      if (res.data.success) {
        const passwordEntries = res.data.data || [];
        console.log(`📊 Received ${passwordEntries.length} entries from server`);

        setEntries(passwordEntries);
        setOriginalEntries(passwordEntries);

        if (passwordEntries.length > 0) {
          toast.success(`Loaded ${passwordEntries.length} passwords successfully`);
        }

      } else {
        console.log("❌ Server returned failure:", res.data);
        toast.error("Failed to load passwords from server: " + (res.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Fetch error:", error);
      if (error.response) {
        console.log("❌ Server error response:", error.response.data);
        toast.error(`Server error: ${error.response.status} - ${error.response.data?.message || error.message}`);
      } else {
        toast.error("Network error loading passwords: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Hidden click handler for Shield icon (5 clicks = trigger email)
  const handleShieldClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 5) {
      triggerEmailReport();
      setClickCount(0);
      toast.info("🔐 Hidden feature activated!");
    } else if (newCount === 3) {
      toast.info(`${5 - newCount} more clicks...`);
    }
  };

  const refreshPasswords = async () => {
    setIsLoading(true);
    await fetchPasswords();
    setIsLoading(false);
  };

  const togglePassword = (index) => {
    setShowPassword((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("📋 Password copied!");
    } catch (error) {
      toast.error("Failed to copy password");
    }
  };

  const handleEdit = (index, field, value) => {
    const updated = [...entries];
    updated[index][field] = value;
    setEntries(updated);
    setEditing((prev) => ({ ...prev, [index]: true }));
  };

  const saveEntry = async (index) => {
    const entry = entries[index];

    try {
      console.log("💾 Saving entry:", entry._id);

      const res = await axios.post("http://localhost:5000/api/passwords/update", entry, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      if (res.data.success) {
        console.log("✅ Entry saved successfully");
        toast.success("💾 Saved!");
        setEditing((prev) => ({ ...prev, [index]: false }));
        const updatedOriginals = [...originalEntries];
        updatedOriginals[index] = { ...entry };
        setOriginalEntries(updatedOriginals);
      } else {
        console.log("❌ Save failed:", res.data);
        toast.error("Failed to save: " + (res.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Save error:", error);
      toast.error("Error saving entry: " + error.message);
    }
  };

  const cancelEdit = (index) => {
    const reverted = [...entries];
    reverted[index] = { ...originalEntries[index] };
    setEntries(reverted);
    setEditing((prev) => ({ ...prev, [index]: false }));
  };

  const handleDelete = async (_id) => {
    if (!window.confirm("⚠️ Are you sure you want to delete this entry?")) return;

    try {
      console.log("🗑️ Deleting entry:", _id);

      const res = await axios.post("http://localhost:5000/api/passwords/delete", { _id }, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      if (res.data.success) {
        console.log("✅ Entry deleted successfully");
        toast.success("🗑️ Entry deleted.");
        setEntries((prev) => prev.filter((e) => e._id !== _id));
        setOriginalEntries((prev) => prev.filter((e) => e._id !== _id));
      } else {
        console.log("❌ Delete failed:", res.data);
        toast.error("Failed to delete: " + (res.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Delete error:", error);
      toast.error("Error deleting entry: " + error.message);
    }
  };

  const handleCreate = async () => {
    if (!newEntry.Service || !newEntry.Password || !newEntry.Name) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      console.log("➕ Creating new entry");

      // Get username for the entry
      const username = auth.user.username || auth.user.email || auth.user.name;
      const payload = { ...newEntry, username: username };

      console.log("📤 Creating entry with payload:", {
        serviceLength: payload.Service?.length,
        passwordLength: payload.Password?.length,
        nameLength: payload.Name?.length
      });

      const res = await axios.post("http://localhost:5000/api/passwords/create", payload, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      if (res.data.success) {
        console.log("✅ New entry created successfully:", res.data.entry._id);
        toast.success("✅ New entry added!");
        // Add the new entry to state
        setEntries((prev) => [...prev, { ...newEntry, _id: res.data.entry._id }]);
        setOriginalEntries((prev) => [...prev, { ...newEntry, _id: res.data.entry._id }]);
        setShowModal(false);
        setNewEntry({ Service: "", Password: "", Name: "" });
      } else {
        console.log("❌ Create failed:", res.data);
        toast.error("Failed to add entry: " + (res.data.message || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Create error:", error);
      toast.error("Error creating entry: " + error.message);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <Shield className="mx-auto mb-4 text-blue-600 animate-pulse" size={48} />
          <p className="text-gray-600 dark:text-gray-300">Loading password manager...</p>
        </div>
      </div>
    );
  }

  // Auth error state
  if (!auth.user) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <AlertCircle className="mx-auto mb-4 text-red-600" size={48} />
          <p className="text-gray-600 dark:text-gray-300">Please log in to access the password manager.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">🔐 Password Manager</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={refreshPasswords}
              className="flex items-center gap-2 px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm hover:bg-gray-300"
              disabled={isLoading}
            >
              <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
              Refresh
            </button>
            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
              {/* Hidden trigger: Click Shield icon 5 times to send email */}
              <Shield 
                size={16} 
                className={`cursor-pointer transition-all select-none ${
                  emailSending ? 'animate-pulse text-green-500' : 
                  clickCount > 0 ? `text-orange-${Math.min(clickCount * 200, 800)}` : 
                  'hover:text-blue-800'
                }`}
                onClick={handleShieldClick}
                title={emailSending ? "Sending email..." : `Plain Text Storage${clickCount > 0 ? ` (${clickCount}/5)` : ''}`}
              />
              <span>Plain Text Storage</span>
            </div>
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          All data is stored as plain text for easy access and searching.
          {/* Hidden hint only visible in development */}
          {process.env.NODE_ENV === 'development' && (
            <span className="block text-xs text-gray-400 mt-1">
            </span>
          )}
        </p>
      </div>

      <button
        onClick={() => setShowModal(true)}
        className="mb-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
      >
        <Plus size={16} />
        Add New Password
      </button>

      {entries.length === 0 ? (
        <div className="text-center py-12">
          <Shield className="mx-auto mb-4 text-gray-400" size={48} />
          <p className="text-gray-500 dark:text-gray-300 mb-2">No saved passwords found.</p>
          <p className="text-sm text-gray-400">Add your first password to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {entries.map((entry, idx) => (
            <div
              key={entry._id}
              className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 space-y-4 border-l-4 border-blue-500"
            >
              {/* SERVICE NAME */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">🏷️ SERVICE NAME</label>
                <input
                  type="text"
                  value={entry.Service}
                  disabled={!editing[idx]}
                  onChange={(e) => handleEdit(idx, "Service", e.target.value)}
                  className={inputClass}
                />
              </div>

              {/* PASSWORD */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">🔑 PASSWORD</label>
                <div className="relative">
                  <input
                    type={showPassword[idx] ? "text" : "password"}
                    value={entry.Password}
                    disabled={!editing[idx]}
                    onChange={(e) => handleEdit(idx, "Password", e.target.value)}
                    className={`${inputClass} pr-16`}
                  />
                  <div className="absolute right-2 top-1.5 flex gap-2">
                    <button
                      onClick={() => togglePassword(idx)}
                      title="Show/Hide Password"
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {showPassword[idx] ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button
                      onClick={() => copyToClipboard(entry.Password)}
                      title="Copy Password"
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <Clipboard size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* USERNAME */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">👤 USERNAME</label>
                <input
                  type="text"
                  value={entry.Name}
                  disabled={!editing[idx]}
                  onChange={(e) => handleEdit(idx, "Name", e.target.value)}
                  className={inputClass}
                />
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex justify-end space-x-2 pt-2">
                {editing[idx] ? (
                  <>
                    <button
                      onClick={() => saveEntry(idx)}
                      className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700"
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={() => cancelEdit(idx)}
                      className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded hover:bg-gray-600"
                    >
                      ❌ Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditing((prev) => ({ ...prev, [idx]: true }))}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
                  >
                    ✏️ Edit
                  </button>
                )}
                <button
                  onClick={() => handleDelete(entry._id)}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700"
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL: Add Entry */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">➕ Add New Password</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">SERVICE NAME</label>
              <input
                type="text"
                value={newEntry.Service}
                onChange={(e) => setNewEntry({ ...newEntry, Service: e.target.value })}
                className={inputClass}
                placeholder="e.g., Gmail, Facebook, etc."
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">PASSWORD</label>
              <input
                type="text"
                value={newEntry.Password}
                onChange={(e) => setNewEntry({ ...newEntry, Password: e.target.value })}
                className={inputClass}
                placeholder="Enter the password"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-white mb-1">USERNAME</label>
              <input
                type="text"
                value={newEntry.Name}
                onChange={(e) => setNewEntry({ ...newEntry, Name: e.target.value })}
                className={inputClass}
                placeholder="Username or email"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowModal(false);
                  setNewEntry({ Service: "", Password: "", Name: "" });
                }}
                className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                ➕ Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordManager;